/* Shared product foundation. No cart writes, guessed prices, or network calls on page load. */
((scope) => {
  const id = value => String(value ?? '').split('/').pop();
  const numericId = value => /^\d+$/.test(id(value)) && Number(id(value)) > 0;
  const money = value => Number.isSafeInteger(value) && value >= 0;
  const path = value => {
    try { return new URL(value, 'https://catalog.invalid').pathname.replace(/\/$/, ''); }
    catch { return ''; }
  };
  function create(data) {
    if (data?.version !== 1 || !Array.isArray(data.families)) throw new Error('catalog_schema');
    const products = new Map(), families = new Map(), issues = [];
    for (const family of data.families) {
      if (!family.key || families.has(family.key)) throw new Error('catalog_family');
      const entries = [];
      for (const raw of family.products || []) {
        if (!numericId(raw.id) || !numericId(raw.productId) || !money(raw.price)) {
          issues.push({ code: 'invalid_product', family: family.key, variantId: id(raw.configuredVariantId) });
          continue;
        }
        const entry = {
          ...raw, id: id(raw.id), productId: id(raw.productId), family: family.key,
          category: family.category, portionKind: family.portionKind,
          available: raw.available === true,
          portionCount: Number.isFinite(raw.portionCount) && raw.portionCount > 0 ? raw.portionCount : null,
          allocations: (raw.allocations || []).filter(a => numericId(a.id) && money(a.price)).map(a => ({ id: id(a.id), price: a.price }))
        };
        // One variant has one identity. Conflicting family assignments must be fixed in the editor.
        if (products.has(entry.id)) throw new Error('catalog_duplicate_variant');
        products.set(entry.id, entry); entries.push(entry.id);
      }
      families.set(family.key, { ...family, products: entries, configured: Boolean(family.source) && entries.length > 0 });
    }
    const box = data.box || {};
    const plans = (box.config?.sellingPlanGroup || []).filter(p => numericId(p.gid) && String(p.interval).toUpperCase() === 'WEEK' && Number.isInteger(Number(p.intervalCount)) && Number(p.intervalCount) > 0)
      .map(p => ({ ...p, id: id(p.gid), weeks: Number(p.intervalCount) }))
      .sort((a, b) => a.weeks - b.weeks);
    function landingFamily(url) {
      const target = path(url);
      for (const family of families.values()) {
        if (family.landingUrl && path(family.landingUrl) === target) return family.key;
      }
      const product = [...products.values()].find(p => p.url && path(p.url) === target);
      return product?.family || (families.has(data.initialFamily) ? data.initialFamily : null);
    }
    function inspectBox(response) {
      const actual = response?.ok === true ? response.DynamicBox : null;
      if (!box.id || !numericId(box.parentProductId) || !actual || actual.boxId !== box.id || !Array.isArray(actual.selectableProductVariants)) return { verified: false, reason: 'box_mismatch', variantIds: [] };
      const variantIds = actual.selectableProductVariants.flatMap(p => (Array.isArray(p?.productVariants) ? p.productVariants : []).map(v => id(v?.platformId)).filter(numericId));
      return { verified: true, variantIds, actual, missingVariantIds: [...products.keys()].filter(v => !variantIds.includes(v)) };
    }
    function selection(quantities, { mode = 'subscription', weeks, boxResponse } = {}) {
      const rows = [], errors = [];
      for (const [variantId, quantity] of quantities) {
        if (!Number.isSafeInteger(quantity) || quantity < 0) { errors.push('quantity'); continue; }
        if (quantity === 0) continue;
        const product = products.get(id(variantId));
        if (!product) { errors.push('unknown_variant'); continue; }
        if (rows.some(row => row.product.id === product.id)) { errors.push('duplicate_variant'); continue; }
        if (!product.available) errors.push('unavailable');
        rows.push({ product, quantity });
      }
      const count = rows.reduce((sum, r) => sum + r.quantity, 0);
      const membership = inspectBox(boxResponse);
      const maximum = box.config?.oneBox && membership.actual?.sizeRange === null ? Infinity : Number(membership.actual?.sizeRange?.[1]);
      if (!Number.isInteger(data.minimumQuantity) || data.minimumQuantity < 1 || (!box.config?.oneBox && !Number.isInteger(maximum)) || maximum < data.minimumQuantity) errors.push('configuration');
      else if (count < data.minimumQuantity || count > maximum) errors.push('quantity_bounds');
      if (!['subscription', 'onetime'].includes(mode)) errors.push('mode');
      const livePlans = membership.actual?.sellingPlanGroup || {};
      const compatiblePlans = plans.map(p => {
        const live = Object.entries(livePlans).find(([key]) => id(key) === p.id)?.[1];
        if (!live || String(live.interval).toUpperCase() !== 'WEEK' || Number(live.intervalCount) !== p.weeks) return null;
        const variantPlanIds = Object.fromEntries(Object.entries(live.variantMapping || {}).map(([key, value]) => [id(key), id(value?.planId)]));
        return { ...p, variantPlanIds };
      }).filter(p => p && rows.every(r => r.product.allocations.some(a => a.id === p.variantPlanIds[r.product.id])));
      const plan = compatiblePlans.find(p => p.weeks === Number(weeks));
      if (mode === 'subscription' && !plan) errors.push('plan');
      if (!membership.verified) errors.push('box_unverified');
      else if (rows.some(r => !membership.variantIds.includes(r.product.id))) errors.push('box_membership');
      const subtotal = rows.reduce((sum, { product, quantity }) => {
        const price = mode === 'onetime' ? product.price : product.allocations.find(a => a.id === plan?.variantPlanIds[product.id])?.price;
        return sum + (price ?? 0) * quantity;
      }, 0);
      const portions = { meal: 0, protein_portion: 0, unknown: false };
      for (const { product, quantity } of rows) {
        if (!product.portionCount || !Object.hasOwn(portions, product.portionKind) || product.portionKind === 'unknown') portions.unknown = true;
        else portions[product.portionKind] += product.portionCount * quantity;
      }
      return { rows, count, compatiblePlans, plan, portions, errors: [...new Set(errors)],
        // Before volume/codes/shipping. Never present an invalid quote as a payable total.
        subtotal: errors.length ? null : subtotal };
    }
    return { version: data.version, currency: data.currency, products, families, plans, box, issues, landingFamily, inspectBox, selection };
  }
  function fromElement(root) {
    const source = root.querySelector('[data-v2-catalog]');
    return source ? create(JSON.parse(source.textContent)) : null;
  }
  const api = { create, fromElement };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else scope.MettaV2Catalog = api;
})(typeof window === 'undefined' ? globalThis : window);
